import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Activity, Package, Users, Factory, RefreshCw, Clock, FileText, ChevronRight, AlertTriangle } from "lucide-react";
import SafetyShell from "../safety/SafetyShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const fmtNum = (n) => new Intl.NumberFormat().format(Number(n || 0));

export default function ProductionLive({ user, onLogout }) {
  const navigate = useNavigate();
  const [dateFrom, setDateFrom] = useState(todayIso());
  const [dateTo, setDateTo] = useState(todayIso());
  const [groupBy, setGroupBy] = useState("product");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/production/live-summary?date_from=${dateFrom}&date_to=${dateTo}&group_by=${groupBy}`,
        authH(),
      );
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load live summary");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, groupBy]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const [unreported, setUnreported] = useState(null);
  useEffect(() => {
    axios.get(`${API}/pallets/unreported-count`, authH())
      .then((r) => setUnreported(r.data))
      .catch(() => {});
  }, []);

  // Auto-refresh every 60s when viewing today
  useEffect(() => {
    if (dateFrom !== todayIso() || dateTo !== todayIso()) return;
    const t = setInterval(fetchData, 60000);
    return () => clearInterval(t);
  }, [dateFrom, dateTo, fetchData]);

  const setQuick = (from, to) => { setDateFrom(from); setDateTo(to); };
  const isToday = dateFrom === todayIso() && dateTo === todayIso();
  const isSingleDay = dateFrom === dateTo;

  // ── Product drill-down: pallets + the DPR each is attached to ──────────────
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillRow, setDrillRow] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const openDrill = async (row) => {
    setDrillRow(row);
    setDrillOpen(true);
    setDrillLoading(true);
    setDrillData(null);
    try {
      const q = new URLSearchParams({
        product_code: row.product_code || "",
        po_no: row.po_number || "",
        date_from: dateFrom,
        date_to: dateTo,
      });
      const r = await axios.get(`${API}/production/live-pallets?${q.toString()}`, authH());
      setDrillData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load pallet details");
    } finally {
      setDrillLoading(false);
    }
  };

  const kpis = data?.kpis || { pallets: 0, pieces: 0, products: 0, operators: 0 };
  const rows = data?.rows || [];
  const hourly = data?.hourly || [];
  const maxHourly = Math.max(1, ...hourly.map((h) => h.pieces));

  return (
    <SafetyShell
      user={user}
      onLogout={onLogout}
      breadcrumb={[{ label: "Safety", to: "/safety" }, { label: "Live Production" }]}
      miniMap={[]}
    >
      <Toaster richColors position="top-right" />
      <div className="bg-white border border-slate-200" data-testid="production-live-page">
        {/* Header */}
        <div className="border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Activity className="w-5 h-5 text-emerald-600" />
            <div>
              <h1 className="text-lg font-semibold text-slate-900">Live Production</h1>
              <p className="text-xs text-slate-500">Real-time output aggregated from pallet scans</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {unreported && unreported.count > 0 && (
              <button
                onClick={() => navigate("/safety/pallets")}
                className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-300 text-amber-800 text-xs px-2.5 py-1 hover:bg-amber-100 transition-colors"
                title="Produced/packed pallets not yet on any Daily Production Report — click to review on the Pallets page"
                data-testid="live-unreported-nudge"
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                {unreported.count} not on a DPR · {unreported.pieces.toLocaleString()} pcs
              </button>
            )}
            {isToday && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">LIVE · auto-refresh 60s</Badge>}
            <Button size="sm" variant="outline" onClick={fetchData} disabled={loading} data-testid="refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="border-b border-slate-200 px-4 py-3 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1">
            <Button size="sm" variant={isToday ? "default" : "outline"} onClick={() => setQuick(todayIso(), todayIso())} data-testid="filter-today">Today</Button>
            <Button size="sm" variant={(dateFrom === daysAgoIso(1) && dateTo === daysAgoIso(1)) ? "default" : "outline"} onClick={() => setQuick(daysAgoIso(1), daysAgoIso(1))} data-testid="filter-yesterday">Yesterday</Button>
            <Button size="sm" variant={(dateFrom === daysAgoIso(6) && dateTo === todayIso()) ? "default" : "outline"} onClick={() => setQuick(daysAgoIso(6), todayIso())} data-testid="filter-7d">Last 7 days</Button>
            <Button size="sm" variant={(dateFrom === daysAgoIso(29) && dateTo === todayIso()) ? "default" : "outline"} onClick={() => setQuick(daysAgoIso(29), todayIso())} data-testid="filter-30d">Last 30 days</Button>
          </div>
          <div className="flex items-center gap-1 ml-2 border-l pl-3 border-slate-200">
            <label className="text-xs text-slate-500">From</label>
            <Input type="date" className="h-8 text-xs w-36" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="date-from" />
            <label className="text-xs text-slate-500 ml-1">To</label>
            <Input type="date" className="h-8 text-xs w-36" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="date-to" />
          </div>
          <div className="flex items-center gap-1 ml-auto">
            <label className="text-xs text-slate-500">Group by:</label>
            <Button size="sm" variant={groupBy === "product" ? "default" : "outline"} onClick={() => setGroupBy("product")} data-testid="group-product">Product</Button>
            <Button size="sm" variant={groupBy === "operator" ? "default" : "outline"} onClick={() => setGroupBy("operator")} data-testid="group-operator">Operator</Button>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-4 gap-3 px-4 py-4">
          <KpiCard icon={Package} label="Pallets Produced" value={fmtNum(kpis.pallets)} color="emerald" testId="kpi-pallets" />
          <KpiCard icon={Factory} label="Total Pieces" value={fmtNum(kpis.pieces)} color="sky" testId="kpi-pieces" />
          <KpiCard icon={Activity} label="Distinct Products" value={fmtNum(kpis.products)} color="amber" testId="kpi-products" />
          <KpiCard icon={Users} label="Operators Active" value={fmtNum(kpis.operators)} color="violet" testId="kpi-operators" />
        </div>

        {/* Hourly text breakdown (single day only) */}
        {isSingleDay && hourly.length > 0 && kpis.pallets > 0 && (
          <div className="border-t border-slate-200 px-4 py-3">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-slate-600" />
              <span className="text-sm font-medium text-slate-700">By Hour</span>
              <span className="text-xs text-slate-400">— pcs produced per hour ({dateFrom})</span>
            </div>
            <div className="grid grid-cols-12 gap-1" data-testid="hourly-breakdown">
              {hourly.filter((h) => h.pieces > 0 || h.pallets > 0).map((h) => (
                <div key={h.hour} className="text-center px-1 py-1 rounded bg-slate-50 border border-slate-100" data-testid={`hour-${h.hour}`}>
                  <div className="text-[10px] text-slate-400">{String(h.hour).padStart(2, "0")}:00</div>
                  <div className="text-sm font-semibold text-slate-900">{fmtNum(h.pieces)}</div>
                  <div className="text-[10px] text-slate-500">{h.pallets} pal</div>
                </div>
              ))}
              {hourly.every((h) => h.pieces === 0 && h.pallets === 0) && (
                <div className="col-span-12 text-center text-xs text-slate-400 py-2">No activity in this window</div>
              )}
            </div>
          </div>
        )}

        {/* Main table */}
        <div className="border-t border-slate-200">
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">
              {groupBy === "product" ? "Output by Product" : "Output by Operator"}
            </span>
            <span className="text-xs text-slate-400">{rows.length} {groupBy === "product" ? "product/PO combinations" : "operators"}</span>
          </div>
          <ScrollArea className="max-h-[55vh]">
            {groupBy === "product" ? (
              <table className="w-full text-sm" data-testid="rows-product">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Product Code</th>
                    <th className="text-left px-4 py-2 font-medium">Description</th>
                    <th className="text-left px-4 py-2 font-medium">PO</th>
                    <th className="text-right px-4 py-2 font-medium">Pallets</th>
                    <th className="text-right px-4 py-2 font-medium">Pieces</th>
                    <th className="text-left px-4 py-2 font-medium">Last produced</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={`${r.product_code}|${r.po_number}|${i}`} className="border-b border-slate-100 hover:bg-emerald-50/50 cursor-pointer" onClick={() => openDrill(r)} data-testid={`row-product-${i}`}>
                      <td className="px-4 py-2 font-mono font-medium text-emerald-700">
                        <span className="inline-flex items-center gap-1">{r.product_code}<ChevronRight className="w-3 h-3 opacity-50" /></span>
                      </td>
                      <td className="px-4 py-2 text-slate-600 truncate max-w-xs">{r.description || "—"}</td>
                      <td className="px-4 py-2">
                        {r.po_number ? (
                          <span className="font-mono text-xs text-slate-700">{r.po_number}</span>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">Unassigned</Badge>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right font-medium">{fmtNum(r.pallets)}</td>
                      <td className="px-4 py-2 text-right font-semibold text-slate-900">{fmtNum(r.pieces)}</td>
                      <td className="px-4 py-2 text-xs text-slate-500">{fmtTime(r.last_at)}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan="6" className="text-center text-sm text-slate-400 py-10">No production in this range</td></tr>
                  )}
                </tbody>
              </table>
            ) : (
              <table className="w-full text-sm" data-testid="rows-operator">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Operator</th>
                    <th className="text-right px-4 py-2 font-medium">Pallets</th>
                    <th className="text-right px-4 py-2 font-medium">Pieces</th>
                    <th className="text-right px-4 py-2 font-medium">Distinct Products</th>
                    <th className="text-left px-4 py-2 font-medium">Last produced</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={`${r.operator}|${i}`} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`row-operator-${i}`}>
                      <td className="px-4 py-2 font-medium text-slate-800">{r.operator}</td>
                      <td className="px-4 py-2 text-right font-medium">{fmtNum(r.pallets)}</td>
                      <td className="px-4 py-2 text-right font-semibold text-slate-900">{fmtNum(r.pieces)}</td>
                      <td className="px-4 py-2 text-right">{fmtNum(r.products)}</td>
                      <td className="px-4 py-2 text-xs text-slate-500">{fmtTime(r.last_at)}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan="5" className="text-center text-sm text-slate-400 py-10">No production in this range</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </ScrollArea>
        </div>
      </div>

      {/* Product drill-down: pallets + attached DPR */}
      <Dialog open={drillOpen} onOpenChange={setDrillOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="live-drill-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-emerald-600" />
              <span className="font-mono">{drillRow?.product_code}</span>
              {drillRow?.po_number ? (
                <span className="text-sm font-normal text-slate-500">· PO {drillRow.po_number}</span>
              ) : (
                <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">Unassigned</Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {drillRow?.description || "Pallets produced in this window and the Daily Production Report each is attached to."}
            </DialogDescription>
          </DialogHeader>

          {drillLoading ? (
            <div className="py-12 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : drillData ? (
            <div className="flex-1 overflow-auto">
              <div className="flex gap-4 text-xs text-slate-500 mb-2 px-1">
                <span><b className="text-slate-800">{drillData.count}</b> pallets</span>
                <span><b className="text-slate-800">{fmtNum(drillData.pieces)}</b> pieces</span>
              </div>
              <table className="w-full text-sm border rounded-md">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Pallet ID</th>
                    <th className="text-right px-3 py-2 font-medium">Qty</th>
                    <th className="text-left px-3 py-2 font-medium">Produced</th>
                    <th className="text-left px-3 py-2 font-medium">Operator</th>
                    <th className="text-left px-3 py-2 font-medium">DPR</th>
                  </tr>
                </thead>
                <tbody data-testid="live-drill-table">
                  {drillData.pallets.length === 0 && (
                    <tr><td colSpan="5" className="text-center text-slate-400 py-8">No pallets found</td></tr>
                  )}
                  {drillData.pallets.map((p) => (
                    <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`live-drill-row-${p.id}`}>
                      <td className="px-3 py-2 font-mono text-xs">{p.id}</td>
                      <td className="px-3 py-2 text-right font-medium">{fmtNum(p.qty)}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{fmtTime(p.paired_at)}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">{p.paired_by_email || "—"}</td>
                      <td className="px-3 py-2 text-xs">
                        {p.report_id ? (
                          <Link
                            to={`/safety/production-reports/${p.report_id}`}
                            className="inline-flex items-center gap-1 text-emerald-700 hover:underline"
                            title={`DPR ${p.report_date || ""} · ${p.report_status || ""}`}
                            data-testid={`live-drill-dpr-${p.id}`}
                          >
                            <FileText className="w-3 h-3" />{p.report_date || "View"}
                          </Link>
                        ) : (
                          <span className="text-amber-600" data-testid={`live-drill-nodpr-${p.id}`}>Not on any DPR</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDrillOpen(false)} data-testid="live-drill-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SafetyShell>
  );
}

function KpiCard({ icon: Icon, label, value, color, testId }) {
  const palettes = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    sky: "bg-sky-50 text-sky-700 border-sky-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
  };
  return (
    <div className={`border rounded-lg p-4 ${palettes[color] || palettes.emerald}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs uppercase tracking-wider opacity-80">{label}</span>
        <Icon className="w-4 h-4 opacity-70" />
      </div>
      <div className="text-3xl font-bold tracking-tight">{value}</div>
    </div>
  );
}
